import React, { useState, useEffect, useMemo } from 'react'
import { isEmpty, isNil } from 'ramda'
import {
  getTextByLang
} from '../../ExternalFields'
import { useStore } from '../../../StateProvider'
import { getExternalFieldsList, getIsExtFldsValid, sortExtFlds } from '../tierFormUtils'
import { Button, Form } from '../../../ui'
import { useTranslation } from 'react-i18next'
import { MAX_JOINT_ACC } from '../../../constants/constants'
import { usePrevious } from '../../utils/hooks'
import styles from '../style.css'

const TierFormNoStepsJoint = ({ isLoadingSubmit, onSubmit, onSubmitJoint, buttonName }) => {
  const [{
    whitelabel: { extFields, customTexts, questionnaireTier },
    validation,
    validation: { tier },
    form,
    form: { accId },
    user,
    user: { questionnairePassed },
    flags: { isDocumentUploaded }
  }, dispatch] = useStore()
  const [fieldsDescription, setFieldsDescription] = useState([])
  const [jointFieldsDescription, setJointFieldsDescription] = useState([])
  const [fields, setFields] = useState([])
  const [isValid, setIsValid] = useState(false)
  const { t } = useTranslation()
  const prevAccId = usePrevious(accId)

  const isDocsFieldExist = useMemo(() => extFields.filter(fld => fld.type !== 'docs').length !== 0, [])

  useEffect(() => {
    if (accId === prevAccId) return
    if (accId > prevAccId) {
      if (prevAccId === 0) {
        setFields(sortExtFlds(getExternalFieldsList(jointFieldsDescription)))
        const newUser = { ...user, ...form[prevAccId] }
        dispatch({ type: 'setUser', user: newUser })
        window.scrollTo(0, 0)
      } else {
        const newUser = {
          ...user,
          jointAccounts: {
            ...user.jointAccounts,
            [prevAccId - 1]: {
              ...user.jointAccounts?.[prevAccId - 1], ...form[prevAccId]
            }
          }
        }
        dispatch({ type: 'setUser', user: newUser })
        window.scrollTo(0, 0)
      }
    }
  }, [accId, user, jointFieldsDescription, form])

  useEffect(() => {
    if (isEmpty(fieldsDescription) || !tier) {
      return
    }
    const curFieldsDescription = accId === 0 ? fieldsDescription : jointFieldsDescription
    setIsValid(getIsExtFldsValid(validation[`tier${tier}`][accId], curFieldsDescription))
  }, [validation, fieldsDescription, jointFieldsDescription, accId])

  useEffect(() => {
    if (!tier) return
    /**
     * @type {ExternalField[]}
     */
    let fldDescriptions = extFields.filter(fld => fld.tier === tier)
    if (questionnaireTier === tier && questionnairePassed) {
      fldDescriptions = fldDescriptions.filter(fld => fld.type !== 'questionnaireSelect')
    }
    setFieldsDescription(fldDescriptions)
    const jointFldDescriptions = extFields.filter(fld =>
      fld.tier === tier && !fld.primaryOnly && fld.type !== 'questionnaireSelect'
    )
    setJointFieldsDescription(jointFldDescriptions)
    if (accId === 0) {
      setFields(sortExtFlds(getExternalFieldsList(fldDescriptions)))
    } else {
      setFields(sortExtFlds(getExternalFieldsList(jointFldDescriptions)))
    }
  }, [extFields, tier])

  const onSubmitPrimaryAcc = () => {
    if (isDocsFieldExist) {
      dispatch({
        type: 'setFlag',
        name: 'isDocumentUploaded',
        value: { ...isDocumentUploaded, [accId]: true }
      })
    }
    const resolve = () => {
      if (isNil(user.jointAccounts) || !isEmpty(user.jointAccounts)) {
        dispatch({ type: 'setAccId', accId: accId + 1 })
      }
    }
    onSubmit().then(resolve).catch(() => {})
  }

  const onSubmitJointAcc = () => {
    if (isDocsFieldExist) {
      dispatch({
        type: 'setFlag',
        name: 'isDocumentUploaded',
        value: { ...isDocumentUploaded, [accId]: false }
      })
    }
    onSubmitJoint({ isLastJointAcc: true }).catch(() => {})
  }

  const addJointAcc = () => {
    const resolve = () => {
      dispatch({ type: 'setAccId', accId: accId + 1 })
    }
    onSubmitJoint({ isLastJointAcc: false }).then(resolve).catch(() => {})
  }

  const goToPreviousAcc = () => {
    const newUser = {
      ...user,
      jointAccounts: {
        ...user.jointAccounts,
        [accId - 1]: {
          ...user.jointAccounts?.[accId - 1], ...form[accId]
        }
      }
    }
    if (isDocsFieldExist) {
      dispatch({
        type: 'setFlag',
        name: 'isDocumentUploaded',
        value: { ...isDocumentUploaded, [accId]: true }
      })
    }
    dispatch({ type: 'setUser', user: newUser })
    if (accId - 1 === 0) {
      setFields(sortExtFlds(getExternalFieldsList(fieldsDescription)))
    }
    dispatch({ type: 'setAccId', accId: accId - 1 })
    window.scrollTo(0, 0)
  }

  const mayHaveJointAccounts = isNil(user.jointAccounts) || !isEmpty(user.jointAccounts)
  return fields.length ? (
    <>
      <div>
        <div className={styles.accType}>
          {accId === 0 ? t('jointAcc.primaryAcc') : t('jointAcc.jointAcc')}
        </div>
        <Form key={`jointAccForm${accId}`}>
          {fields}
          <div className={styles.buttons}>
            {accId > 0 && !user.docCheckPassed && (
              <Button
                style='back'
                onClick={goToPreviousAcc}
                disabled={!isValid || isLoadingSubmit}
              >
                {t('jointAcc.toPrevAccount')}
              </Button>
            )}
            {accId > 0 && accId < MAX_JOINT_ACC && (
              <Button
                style='next'
                onClick={addJointAcc}
                disabled={!isValid || isLoadingSubmit}
              >
                {isNil(user.jointAccounts?.[accId]) ? t('jointAcc.addJointAccount') : t('jointAcc.nextJointAccount')}
              </Button>
            )}
            {accId === 0 && mayHaveJointAccounts && (
              <Button
                style='next'
                disabled={!isValid || isLoadingSubmit}
                onClick={onSubmitPrimaryAcc}
                isLoading={isLoadingSubmit}
              >
                {t('jointAcc.continue')}
              </Button>
            )}
            {accId === 0 && !mayHaveJointAccounts && (
              <Button
                disabled={!isValid || isLoadingSubmit}
                onClick={onSubmitPrimaryAcc}
                isLoading={isLoadingSubmit}
              >
                {buttonName}
              </Button>
            )}
            {accId > 0 && (
              <Button
                disabled={!isValid || isLoadingSubmit}
                onClick={onSubmitJointAcc}
                isLoading={isLoadingSubmit}
              >
                {buttonName}
              </Button>)}
          </div>
        </Form>
      </div>

      <div className={styles.additionalText}>
        {getTextByLang('additionalConditions', customTexts)}
      </div>
    </>
  ) : null
}

export { TierFormNoStepsJoint }
